<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import dayjs from "dayjs";

import SegmentDO = App.Data.Segments.SegmentDO;
import SegmentExecutionDetailsDTO = App.Data.Integrations.SegmentExecutionDetailsDTO;
import { numericAndFloat, required } from "@/ui/validation/validationRules";
import { t } from "@/plugins/i18n";

const props = defineProps<{
	sourceData: SegmentDO;
}>();

const executionProperties = defineModel<SegmentExecutionDetailsDTO | null>();

const inputValid = defineModel<boolean>("inputValid", {
	default: () => ref(false),
});

onMounted(() => {
	// the exportDate is set here as a placeholder, it will be overwritten right before the export is triggered
	if (!executionProperties.value) {
		executionProperties.value = {
			type: "segment",
			uuid: props.sourceData.uuid,
			configuration: {
				exportDate: dayjs().format("YYYY-MM-DD"),
				isSizeLimitEnabled: false,
				sizeLimit: 0,
			},
		};
	}

	// no validation needed in this component
	inputValid.value = true;
});

const localIsSizeLimitEnabled = computed<boolean>({
	get() {
		return executionProperties.value?.configuration.isSizeLimitEnabled ?? false;
	},
	set(v) {
		executionProperties.value.configuration.isSizeLimitEnabled = v;
	},
});

const localIsSizeLimit = computed<number>({
	get() {
		return executionProperties.value?.configuration.sizeLimit ?? 0;
	},
	set(v) {
		executionProperties.value.configuration.sizeLimit = v;
	},
});
</script>

<template>
	<div class="pb-3 d-flex">
		<h6 class="text-h6 label">Identifier:</h6>
		<div>{{ sourceData.uuid }}</div>
	</div>
	<div class="pb-3 d-flex">
		<h6 class="text-h6 label">Title:</h6>
		<div>{{ sourceData.title }}</div>
	</div>
	<div class="pb-3 d-flex">
		<h6 class="text-h6 label">Description:</h6>
		<div>{{ sourceData.description }}</div>
	</div>
	<v-divider class="mb-3" />
	<div class="pb-3 d-flex">
		<h6 class="text-h6 label mt-3">Restrict Size:</h6>
		<div>
			<v-radio-group
				hide-details="auto"
				v-model="localIsSizeLimitEnabled"
				inline
			>
				<v-radio :value="false" :label="`Use entire segment`"> </v-radio>
				<v-radio
					:value="true"
					:label="`Restrict export to a certain amount`"
				></v-radio>
			</v-radio-group>
		</div>
	</div>
	<v-fade-transition>
		<div v-if="localIsSizeLimitEnabled" class="pb-3 d-flex">
			<h6 class="text-h6 label mt-3">Size:</h6>
			<v-text-field
				:rules="[required(t), numericAndFloat(t)]"
				hide-details="auto"
				variant="filled"
				append-inner-icon="fa: fa-light fa-user"
				v-model="localIsSizeLimit"
			/>
		</div>
	</v-fade-transition>
</template>

<style lang="scss" scoped>
.label {
	width: 250px;
}
</style>
